import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CLIENT_COLORS } from './constants';

export function ClientResourcePanel({ timeline }) {
  const totalWeeks = timeline.total_duration_weeks;
  const weekNums = Array.from({ length: totalWeeks }, (_, i) => i + 1);
  const resources = timeline.client_resources || [];
  const getCellBg = (pct) => {
    if (pct === 0) return { bg: 'transparent', text: '#94A3B8' };
    if (pct >= 50) return { bg: '#0F2040', text: '#FFFFFF' };
    if (pct >= 25) return { bg: '#1E3A5F', text: '#E2E8F0' };
    if (pct >= 10) return { bg: '#334E6F', text: '#CBD5E1' };
    return { bg: '#4A6580', text: '#CBD5E1' };
  };

  return (
    <>
      <Card data-testid="client-resource-heatmap">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#0F2040]">Client Resource Allocation — % Time by Week</CardTitle>
          <p className="text-[10px] text-slate-400 mt-0.5">Expected client team involvement per week.</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] border-separate" style={{ borderSpacing: '2px' }}>
              <thead>
                <tr>
                  <th className="text-left px-2 py-1.5 text-slate-500 font-medium w-44">Client Role</th>
                  {weekNums.map(w => (<th key={w} className="text-center px-1 py-1.5 text-slate-500 font-medium min-w-[48px]">W{w}</th>))}
                  <th className="text-center px-2 py-1.5 text-slate-500 font-medium min-w-[48px]">Avg</th>
                  <th className="text-center px-2 py-1.5 text-slate-500 font-medium min-w-[48px]">Peak</th>
                </tr>
              </thead>
              <tbody>
                {resources.map(cr => {
                  const weekly = cr.weekly_pct || {};
                  return (
                    <tr key={cr.role}>
                      <td className="px-2 py-1.5 font-medium text-[#0F2040]">
                        <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: CLIENT_COLORS[cr.role] || '#94A3B8' }} />
                        {cr.label}
                      </td>
                      {weekNums.map(w => {
                        const pct = weekly[w] || 0;
                        const colors = getCellBg(pct);
                        return (
                          <td key={w} className="text-center py-1.5 rounded-sm font-mono transition-colors" data-testid={`client-${cr.role}-w${w}`}
                            style={{ backgroundColor: colors.bg, color: colors.text, fontSize: '10px', border: '1px solid transparent' }}>
                            {pct > 0 ? `${pct}%` : '—'}
                          </td>
                        );
                      })}
                      <td className="text-center py-1.5 font-semibold text-[#0F2040]">{cr.avg_commitment ? `${cr.avg_commitment}%` : '—'}</td>
                      <td className="text-center py-1.5 text-[10px]">
                        {cr.peak_week ? <span className="inline-flex items-center gap-0.5"><span className="font-semibold text-[#0F2040]">W{cr.peak_week}</span><span className="text-slate-400">({cr.peak_pct}%)</span></span> : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="client-resource-table">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#0F2040]">Client Resource Summary</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#0F2040] text-white">
                  <th className="text-left px-3 py-2 rounded-tl-md">Client Role</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2">Active Weeks</th>
                  <th className="text-right px-3 py-2">Avg Commitment</th>
                  <th className="text-right px-3 py-2 rounded-tr-md">Peak Week</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((cr, i) => (
                  <tr key={cr.role} className={i % 2 ? 'bg-slate-50' : ''}>
                    <td className="px-3 py-2 font-medium">
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ backgroundColor: CLIENT_COLORS[cr.role] || '#94A3B8' }} />
                      {cr.label}
                    </td>
                    <td className="px-3 py-2 text-slate-500 max-w-[200px]">{cr.description}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{cr.active_week_ranges || '—'}</td>
                    <td className="text-right px-3 py-2 font-semibold">{cr.avg_commitment}%</td>
                    <td className="text-right px-3 py-2">
                      {cr.peak_week ? <span>W{cr.peak_week} <span className="text-slate-400">({cr.peak_pct}%)</span></span> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
